import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useState, useEffect, useCallback } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, ShoppingBag, Search, ChevronDown, ChevronUp, Trash2, Edit2 } from 'lucide-react';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

export default function HesapSalesReportPage() {
  const { token } = useHesapAuth();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [search, setSearch] = useState('');
  const [expandedSale, setExpandedSale] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Düzenleme
  const [editingSale, setEditingSale] = useState(null);
  const [editForm, setEditForm] = useState({ discount: 0, note: '', payment_method: 'nakit', items: [] });
  const [saving, setSaving] = useState(false);

  const loadSales = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await fetch(`${API}/sales?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSales(data.sales || []);
        setTotalCount(data.total || 0);
      }
    } catch { toast.error('Satışlar yüklenemedi'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, page]);

  useEffect(() => { loadSales(); }, [loadSales]);

  const deleteSale = async (id) => {
    if (!window.confirm('Bu satışı silmek istediğinize emin misiniz?')) return;
    try {
      await fetch(`${API}/sales/${id}`, { method: 'DELETE', headers });
      toast.success('Satış silindi');
      loadSales();
    } catch { toast.error('Silme başarısız'); }
  };

  const openEdit = (sale) => {
    setEditingSale(sale);
    setEditForm({
      discount: sale.discount || 0,
      note: sale.note || '',
      payment_method: sale.payment_method || 'nakit',
      items: (sale.items || []).map(item => ({
        product_id: item.product_id || '',
        product_name: item.product_name || '',
        quantity: item.quantity || 1,
        unit_price: item.unit_price || 0,
        total: item.total || 0
      }))
    });
  };

  const updateEditItem = (idx, field, value) => {
    setEditForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        items[idx].total = (items[idx].unit_price || 0) * (items[idx].quantity || 1);
      }
      return { ...prev, items };
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const totalAmount = editForm.items.reduce((s, i) => s + (i.total || 0), 0);
      const body = {
        discount: editForm.discount,
        note: editForm.note,
        payment_method: editForm.payment_method,
        items: editForm.items,
        total_amount: totalAmount
      };
      const res = await fetch(`${API}/sales/${editingSale.id}`, { method: 'PUT', headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      toast.success('Satış güncellendi');
      setEditingSale(null);
      loadSales();
    } catch { toast.error('Güncelleme başarısız'); }
    finally { setSaving(false); }
  };

  const filtered = sales.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.customer_name || '').toLowerCase().includes(q) ||
      (s.items || []).some(i => (i.product_name || '').toLowerCase().includes(q));
  });

  // Özet - net_amount kullan (indirimli fiyat)
  const totalRevenue = filtered.reduce((s, sale) => s + (sale.net_amount || sale.total_amount || 0), 0);
  const totalDiscount = filtered.reduce((s, sale) => s + (sale.discount || 0), 0);
  const cashSales = filtered.filter(s => s.payment_method === 'nakit');
  const cardSales = filtered.filter(s => s.payment_method === 'kart');
  const veresiyeSales = filtered.filter(s => s.is_veresiye);

  const paymentLabel = (m) => {
    if (m === 'nakit') return 'Nakit';
    if (m === 'kart') return 'Kart';
    return m || 'Nakit';
  };

  const editTotalAmount = editForm.items.reduce((s, i) => s + (i.total || 0), 0);
  const editNetAmount = editTotalAmount - (editForm.discount || 0);

  return (
    <div className="space-y-4" data-testid="sales-report-page">
      <h1 className="text-2xl font-bold text-slate-800">Satış Raporu</h1>

      {/* Filtre */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Başlangıç</label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40" data-testid="date-from" />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Bitiş</label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40" data-testid="date-to" />
        </div>
        <div className="flex-1 min-w-[150px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Ürün veya müşteri ara..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
          </div>
        </div>
        <Button variant="outline" onClick={loadSales}><Loader2 className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Yenile</Button>
      </div>

      {/* Özet Kartlar */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card><CardContent className="p-3 text-center">
          <p className="text-xs text-slate-500">Toplam Satış</p>
          <p className="text-xl font-bold text-slate-800">{filtered.length}</p>
        </CardContent></Card>
        <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-3 text-center">
          <p className="text-xs text-emerald-700">Net Gelir</p>
          <p className="text-xl font-bold text-emerald-700">{totalRevenue.toLocaleString('tr-TR')} TL</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-xs text-slate-500">Nakit</p>
          <p className="text-lg font-bold text-green-600">{cashSales.reduce((s, c) => s + (c.net_amount || c.total_amount || 0), 0).toLocaleString('tr-TR')} TL</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-xs text-slate-500">Kart</p>
          <p className="text-lg font-bold text-blue-600">{cardSales.reduce((s, c) => s + (c.net_amount || c.total_amount || 0), 0).toLocaleString('tr-TR')} TL</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-xs text-slate-500">Veresiye</p>
          <p className="text-lg font-bold text-orange-600">{veresiyeSales.reduce((s, c) => s + (c.net_amount || c.total_amount || 0), 0).toLocaleString('tr-TR')} TL</p>
        </CardContent></Card>
      </div>

      {totalDiscount > 0 && (
        <Card className="border-amber-200 bg-amber-50"><CardContent className="p-2 text-center">
          <p className="text-sm text-amber-700">Toplam İndirim: <span className="font-bold">{totalDiscount.toLocaleString('tr-TR')} TL</span></p>
        </CardContent></Card>
      )}

      {/* Satış Listesi */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <ShoppingBag className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">Bu tarihte satış bulunamadı</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(sale => (
            <Card key={sale.id} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50"
                  onClick={() => setExpandedSale(expandedSale === sale.id ? null : sale.id)}>
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${sale.is_veresiye ? 'bg-orange-500' : sale.payment_method === 'kart' ? 'bg-blue-500' : 'bg-green-500'}`} />
                    <div>
                      <p className="text-sm font-medium">
                        {new Date(sale.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                        {sale.customer_name && <span className="ml-2 text-slate-500">- {sale.customer_name}</span>}
                      </p>
                      <p className="text-xs text-slate-400">
                        {(sale.items || []).map(i => i.product_name).filter(Boolean).join(', ') || `${(sale.items || []).length} ürün`}
                        {' | '}{paymentLabel(sale.payment_method)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {sale.discount > 0 && (
                      <span className="text-xs line-through text-slate-400">{(sale.total_amount || 0).toLocaleString('tr-TR')}</span>
                    )}
                    <span className="font-bold">{(sale.net_amount || sale.total_amount || 0).toLocaleString('tr-TR')} TL</span>
                    {sale.is_veresiye && <span className="text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">Veresiye</span>}
                    {expandedSale === sale.id ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                  </div>
                </div>

                {expandedSale === sale.id && (
                  <div className="border-t p-3 bg-slate-50">
                    <table className="w-full text-sm">
                      <thead><tr className="text-xs text-slate-500">
                        <th className="text-left py-1">Ürün</th>
                        <th className="text-center py-1">Adet</th>
                        <th className="text-right py-1">Birim Fiyat</th>
                        <th className="text-right py-1">Toplam</th>
                      </tr></thead>
                      <tbody>
                        {(sale.items || []).map((item, i) => (
                          <tr key={i} className="border-t border-slate-200">
                            <td className="py-1 font-medium">{item.product_name || '-'}</td>
                            <td className="text-center py-1">{item.quantity}</td>
                            <td className="text-right py-1">{(item.unit_price || 0).toLocaleString('tr-TR')} TL</td>
                            <td className="text-right py-1 font-medium">{(item.total || (item.unit_price || 0) * (item.quantity || 1)).toLocaleString('tr-TR')} TL</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {sale.discount > 0 && (
                      <div className="mt-2 flex justify-between text-sm border-t pt-1">
                        <span className="text-slate-500">Ara Toplam:</span>
                        <span>{(sale.total_amount || 0).toLocaleString('tr-TR')} TL</span>
                      </div>
                    )}
                    {sale.discount > 0 && (
                      <div className="flex justify-between text-sm text-amber-600">
                        <span>İndirim:</span>
                        <span>-{(sale.discount || 0).toLocaleString('tr-TR')} TL</span>
                      </div>
                    )}
                    {sale.discount > 0 && (
                      <div className="flex justify-between text-sm font-bold border-t pt-1">
                        <span>Net Tutar:</span>
                        <span>{(sale.net_amount || 0).toLocaleString('tr-TR')} TL</span>
                      </div>
                    )}
                    {sale.note && <p className="text-xs text-slate-500 mt-1">Not: {sale.note}</p>}
                    <div className="mt-2 flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(sale)} data-testid="edit-sale-btn">
                        <Edit2 className="h-3.5 w-3.5 mr-1" />Düzenle
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-500 border-red-200 hover:bg-red-50" onClick={() => deleteSale(sale.id)}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" />Sil
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {totalCount > 50 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Önceki</Button>
          <span className="text-sm text-slate-500 flex items-center">Sayfa {page}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)}>Sonraki</Button>
        </div>
      )}

      {/* Satış Düzenleme Dialog */}
      <Dialog open={!!editingSale} onOpenChange={(v) => { if (!v) setEditingSale(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Satış Düzenle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* Ürün listesi */}
            <div>
              <Label className="text-xs text-slate-500">Ürünler</Label>
              <div className="space-y-2 mt-1">
                {editForm.items.map((item, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50 p-2 rounded">
                    <div className="col-span-5">
                      <Input value={item.product_name} onChange={e => updateEditItem(i, 'product_name', e.target.value)} placeholder="Ürün adı" className="text-sm h-8" />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" value={item.quantity} onChange={e => updateEditItem(i, 'quantity', parseInt(e.target.value) || 0)} className="text-sm h-8 text-center" />
                    </div>
                    <div className="col-span-3">
                      <Input type="number" step="0.01" value={item.unit_price} onChange={e => updateEditItem(i, 'unit_price', parseFloat(e.target.value) || 0)} className="text-sm h-8 text-right" />
                    </div>
                    <div className="col-span-2 text-right text-sm font-medium">
                      {(item.total || 0).toLocaleString('tr-TR')} TL
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>İndirim (TL)</Label>
                <Input type="number" step="0.01" value={editForm.discount} onChange={e => setEditForm({ ...editForm, discount: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-1">
                <Label>Ödeme Yöntemi</Label>
                <select value={editForm.payment_method} onChange={e => setEditForm({ ...editForm, payment_method: e.target.value })}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="nakit">Nakit</option>
                  <option value="kart">Kart</option>
                </select>
              </div>
            </div>

            <div className="space-y-1">
              <Label>Not</Label>
              <Input value={editForm.note} onChange={e => setEditForm({ ...editForm, note: e.target.value })} placeholder="Satış notu..." />
            </div>

            {/* Özet */}
            <div className="bg-slate-100 p-3 rounded-lg space-y-1 text-sm">
              <div className="flex justify-between"><span>Ara Toplam:</span><span>{editTotalAmount.toLocaleString('tr-TR')} TL</span></div>
              {editForm.discount > 0 && (
                <div className="flex justify-between text-amber-600"><span>İndirim:</span><span>-{editForm.discount.toLocaleString('tr-TR')} TL</span></div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-1">
                <span>Net Tutar:</span>
                <span className="text-emerald-700">{editNetAmount.toLocaleString('tr-TR')} TL</span>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditingSale(null)} className="flex-1">İptal</Button>
              <Button onClick={saveEdit} disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kaydet'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
